import clsx from "clsx";
import { GatsbyImage } from "gatsby-plugin-image";
import React, { DetailedHTMLProps, FC, HTMLAttributes } from "react";

import { Cta } from "../../../buttons/cta";
import { Pill } from "../../../forms";
import { TagDimensions } from "../../../forms/elements/tags/types";
import { RichText } from "../../../rich-text";
import { ImageCardProps } from "../image-card";
import { defaultCardColorScheme } from "../styles";
import { ImageCardBaseVariants, ImageCardVariants } from "../types";

export interface DefaultCardProps
  extends Queries.ImageCardDataFragment,
    Pick<ImageCardProps, "keepOriginalAspectRatio" | "showBody" | "tags" | "textAlign">,
    Pick<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, "className"> {}

export const DefaultCard: FC<DefaultCardProps> = ({
  title,
  description,
  image,
  logo,
  ctas,
  variant = ImageCardBaseVariants.Default,
  className,
  keepOriginalAspectRatio = true,
  showBody = true,
  textAlign = "left",
  tags,
}) => {
  const { backgroundColorClassName } = variant
    ? defaultCardColorScheme[variant as ImageCardVariants]
    : {
        backgroundColorClassName: ImageCardBaseVariants.Default,
      };

  const ctasCount = (ctas ?? []).length;

  const card = (
    <div
      className={clsx(
        "relative rounded-xl overflow-hidden group",
        keepOriginalAspectRatio ? "aspect-[3/4]" : "h-full aspect-[3/4] lg:aspect-auto",
        className
      )}
    >
      {logo?.gatsbyImageData && (
        <div className="absolute top-0 left-0 z-30 w-3/4 p-[5%]">
          <GatsbyImage
            image={logo.gatsbyImageData}
            alt=""
            className="max-h-32"
            imgClassName="!object-contain object-left"
          />
        </div>
      )}

      <div
        className={clsx(
          "absolute z-20 bottom-0 w-full transition-opacity",
          "flex flex-col gap-3",
          !showBody && "opacity-0"
        )}
      >
        {tags?.length && (
          <div className="flex gap-2 justify-center w-full flex-wrap">
            {tags.map(({ color, children }, i) => {
              return (
                <Pill key={i} color={color} dimension={TagDimensions.Medium} checked readOnly>
                  {children}
                </Pill>
              );
            })}
          </div>
        )}
        <div className="relative px-8 py-10">
          {/* Bottom overlay layer: background and secondary image */}
          <span
            className={clsx(
              "absolute bottom-0 left-0",
              "transition-all group-hover:h-full",
              "w-full h-3 -z-10",
              backgroundColorClassName
            )}
          />

          <div
            className={clsx(
              "text-white",
              textAlign === "center" && "text-center",
              variant &&
                !(Object.values(ImageCardBaseVariants) as string[]).includes(variant) &&
                "group-hover:text-dark transition-colors"
            )}
          >
            <h3 className="font-medium text-4xl mb-2 z-10">{title}</h3>
            {description?.raw && (
              <RichText raw={description.raw} className="font-light text-xl line-clamp-[9]" />
            )}
          </div>
          {ctasCount > 1 && (
            <div
              className={clsx(
                "max-h-0 overflow-hidden opacity-0 transition-all duration-300",
                "group-hover:opacity-100 group-hover:max-h-40 group-hover:md:max-h-44"
              )}
            >
              {(ctas ?? []).length > 0 && (
                <div className="flex flex-col gap-4 h-auto">
                  {ctas
                    ?.filter(Boolean)
                    .map((cta, index) => (
                      <Cta key={index} variant="light-outline" {...cta!} fullWidth />
                    ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Gradient */}
      <div className="absolute z-10 w-full h-full bg-gradient-to-b from-transparent to-[rgba(0,0,0,0.5)]" />
      {/* main background image */}
      {image?.gatsbyImageData && (
        <GatsbyImage image={image.gatsbyImageData} alt={title ?? ""} className="w-full h-full" />
      )}
    </div>
  );

  return ctasCount === 1 ? <Cta {...ctas![0]!}>{card}</Cta> : card;
};
